import React, { useCallback, useEffect, useState } from "react";
import BlockIcon from "@mui/icons-material/Block";
import TableChartIcon from "@mui/icons-material/TableChart";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setFilteredNavData } from "../../../../../store/slices/LeadreportfilteredSlice";
import { setFilteredReports } from "../../../../../store/slices/filteredDataSlice";
import { FirstLetterCapital } from "../../../../../utils/FirstLetterCapital";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import CommaConvert from "../../../../../utils/CommaConvert";

const DashStatusCards = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [filteredLeadReports, setFilteredLeadReports] = useState([]);

  const { filteredReports } = useSelector((state) => state.filteredData);

  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();

  useEffect(() => {
    if (filteredReports) {
      setFilteredLeadReports(filteredReports);
    }
    dispatch(setFilteredReports())
  }, [filteredReports, dispatch]);

  const [LeadCount, setLeadCount] = useState(0);
  const [totalQuotedValue, setTotalQuotedValue] = useState(0);
  const [wonLeadCount, setWonLeadCount] = useState(0);
  const [wonTotalQuotedValue, setWonTotalQuotedValue] = useState(0);
  const [wonRecurringLeadCount, setWonRecurringLeadCount] = useState(0);
  const [wonRecurringTotalQuotedValue, setWonRecurringTotalQuotedValue] =
    useState(0);
  const [pendingLeadCount, setPendingLeadCount] = useState(0);
  const [pendingTotalQuotedValue, setPendingTotalQuotedValue] = useState(0);
  const [lostLeadCount, setLostLeadCount] = useState(0);
  const [lostTotalQuotedValue, setLostTotalQuotedValue] = useState(0);

  useEffect(() => {
    // Update the count of filtered leads
    setLeadCount(filteredLeadReports.length);

    // Calculate the total QuotedValue
    const total = filteredLeadReports.reduce(
      (sum, lead) => sum + (lead.QuotedValue || 0),
      0
    );
    setTotalQuotedValue(total);

    // Filter and calculate count and total for leads with "Won" status
    const wonLeads = filteredLeadReports.filter(
      (lead) => lead.Status?.trim().toLowerCase() === "won"
    );
    setWonLeadCount(wonLeads.length);

    const wonTotal = wonLeads.reduce(
      (sum, lead) => sum + (lead.QuotedValue || 0),
      0
    );
    setWonTotalQuotedValue(wonTotal);

    const wonRecurringLeads = filteredLeadReports.filter(
      (lead) => lead.Status?.trim().toLowerCase() === "won recurring"
    );
    setWonRecurringLeadCount(wonRecurringLeads.length);

    const wonRecurringTotal = wonRecurringLeads.reduce(
      (sum, lead) => sum + (lead.QuotedValue || 0),
      0
    );
    setWonRecurringTotalQuotedValue(wonRecurringTotal);

    const pendingLeads = filteredLeadReports.filter(
      (lead) => lead.Status?.trim().toLowerCase() === "pending" 
    );
    setPendingLeadCount(pendingLeads.length);

    const pendingTotal = pendingLeads.reduce(
      (sum, lead) => sum + (lead.QuotedValue || 0),
      0
    );
    setPendingTotalQuotedValue(pendingTotal);

    const lostLeads = filteredLeadReports.filter(
      (lead) => lead.Status?.trim().toLowerCase() === "lost"
        
    );
    setLostLeadCount(lostLeads.length);

    const lostTotal = lostLeads.reduce(
      (sum, lead) => sum + (lead.QuotedValue || 0),
      0
    );
    setLostTotalQuotedValue(lostTotal);
  }, [filteredLeadReports]);


  const handleAllLeads = useCallback(
    (clickedDashStatus) => {
      let AllLeadsL1;
      if (clickedDashStatus === "All") {
        AllLeadsL1 = filteredLeadReports; // Show all data
      } else {
        AllLeadsL1 = filteredLeadReports.filter(
          (item) => FirstLetterCapital(item.Status) === clickedDashStatus
        );
      }
      dispatch(setFilteredNavData(AllLeadsL1));

      navigate(`/dashboard/lead-report`);
    },
    [filteredLeadReports, navigate, dispatch]
  );

  return (
    <div className="w-[100%]">
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 pt-2 rounded-md">
        <div className="bg-white border shadow-md rounded-lg  ">
          <div
            onClick={() => {
              handleAllLeads("All");
            }}
            className="flex justify-between items-center rounded-t-lg  bg-[#e32278] text-white text-left p-2"
            style={{ cursor: "pointer" }}
          >
            <h2 className="text-lg font-semibold">All</h2>
            <h1 className="text-2xl font-bold">{LeadCount}</h1>

            <TableChartIcon
              alt="handshake icon"
              className="w-12 h-9 bottom-2 right-2 "
            />
          </div>
          <div className="p-4 flex justify-center items-center">
            <div className="flex-col">
              <div className="flex justify-center items-center gap-4">
                <p className="text-lg mt-2 text-center font-bold text-wrap break-all whitespace-normal">
                  {currency}
                  {USAComma(totalQuotedValue)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-md overflow-hidden ">
          <div
            onClick={() => {
              handleAllLeads("Won");
            }}
            className=" flex justify-between items-center bg-[#157f3d] text-white text-left p-2"
            style={{ cursor: "pointer" }}
          >
            <h2 className="text-lg font-semibold">Won</h2>
            <h1 className="text-2xl font-bold">{wonLeadCount}</h1>
            <EmojiEventsIcon
              alt="handshake icon"
              className="w-12 h-9 bottom-2 right-2"
            />
          </div>
          <div className="p-4 flex justify-center items-center">
            <div className="flex-col">
              <div className="flex gap-4">
                <p className="text-lg mt-2 font-bold">
                  {currency}
                  {USAComma(wonTotalQuotedValue)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-md overflow-hidden ">
          <div
            onClick={() => {
              handleAllLeads("Won Recurring");
            }}
            className="flex justify-between items-center bg-[#1f41bb] text-white text-left p-2"
            style={{ cursor: "pointer" }}
          >
            <h2 className="text-lg font-semibold">Won Rec</h2>
            <h1 className="text-2xl font-bold">{wonRecurringLeadCount}</h1>
            <PublishedWithChangesIcon
              alt="handshake icon"
              className="w-12 h-9 bottom-2 right-2"
            />
          </div>
          <div className="p-4 flex justify-center items-center">
            <div className="flex-col">
              <div className="flex gap-4">
                <p className="text-lg mt-2 font-bold">
                  {currency}
                  {USAComma(wonRecurringTotalQuotedValue)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-md overflow-hidden ">
          <div
            onClick={() => {
              handleAllLeads("Pending");
            }}
            className="flex justify-between items-center bg-[#ff9a00] text-white text-left p-2"
            style={{ cursor: "pointer" }}
          >
            <h2 className="text-lg font-semibold">Pending</h2>
            <h1 className="text-2xl font-bold">{pendingLeadCount}</h1>

            <PendingActionsIcon
              alt="handshake icon"
              className="w-12 h-9 bottom-2 right-2"
            />
          </div>
          <div className="p-4 flex justify-center items-center">
            <div className="flex-col">
              <div className="flex gap-4">
                <p className="text-lg mt-2 font-bold">
                  {currency}
                  {USAComma(pendingTotalQuotedValue)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-md overflow-hidden ">
          <div
            onClick={() => {
              handleAllLeads("Lost");
            }}
            className="flex justify-between items-center bg-[#ba1c1d] text-white text-left p-2"
            style={{ cursor: "pointer" }}
          >
            <h2 className="text-lg font-semibold">Lost</h2>
            <h1 className="text-2xl font-bold">{lostLeadCount}</h1>
            <BlockIcon
              alt="handshake icon"
              className="w-9 h-7  bottom-2 right-2"
            />
          </div>
          <div className="p-4 flex justify-center items-center">
            <div className="flex-col">
              <div className="flex gap-4">
                <p className="text-lg mt-2 font-bold">
                  {currency}
                  {USAComma(lostTotalQuotedValue)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashStatusCards;
